"use client";

import * as React from "react";
import { Plus, SlidersHorizontal, X } from "lucide-react";
import {
  AddToCartButton,
  EcommerceProductsProvider,
  ProductSortDropdown,
  ProductsFilter,
  useEcommerceProducts,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link } from "@/components/common/Link";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

declare const wvcClient: any;

const wp_query_params = wvcClient.getCurrentWPQueryParams();
const wp_query = new WP_Query(wp_query_params);

interface ProductForDialog {
  id: number;
  title: string;
  short_description?: string;
  long_description?: string;
  price: number;
  featured_image_url?: string;
  link?: string;
}

function ProductsGridConsumer() {
  const { products, loading, error, refetch, total } = useEcommerceProducts();
  const [selectedProduct, setSelectedProduct] =
    React.useState<ProductForDialog | null>(null);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            data-index={i}
            className="aspect-[4/5] animate-pulse bg-secondary"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-border p-8 text-center">
        <p className="font-default text-xs uppercase tracking-[0.18em] text-muted-foreground">
          Loading error
        </p>
        <p className="mt-3 text-foreground">Failed to load our mist collection.</p>
        <Button
          onClick={refetch}
          variant="outline"
          className="mt-4 rounded-lg border-sage-deep font-default text-xs uppercase tracking-[0.16em]"
        >
          Retry
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="border border-border p-12 text-center">
        <p className="font-default text-xs uppercase tracking-[0.18em] text-muted-foreground">
          § — No items found
        </p>
        <p className="mt-4 font-default text-2xl text-foreground">
          No products <em className="italic">currently available</em>
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-6 flex items-center justify-between border-b border-border pb-4">
        <p className="font-default text-xs uppercase tracking-[0.16em] text-muted-foreground">
          {total} {total === 1 ? "Product" : "Products"}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => (
          <article
            key={product.id}
            data-index={i}
            className="group relative"
          >
            <span className="pointer-events-none absolute -left-2 -top-2 z-10 h-3 w-3 text-border transition-all duration-300 group-hover:-left-3 group-hover:-top-3 group-hover:text-accent-gold">
              <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-current" />
              <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-current" />
            </span>
            <span className="pointer-events-none absolute -right-2 -top-2 z-10 h-3 w-3 text-border transition-all duration-300 group-hover:-right-3 group-hover:-top-3 group-hover:text-accent-gold">
              <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-current" />
              <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-current" />
            </span>
            <span className="pointer-events-none absolute -bottom-2 -left-2 z-10 h-3 w-3 text-border transition-all duration-300 group-hover:-bottom-3 group-hover:-left-3 group-hover:text-accent-gold">
              <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-current" />
              <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-current" />
            </span>
            <span className="pointer-events-none absolute -bottom-2 -right-2 z-10 h-3 w-3 text-border transition-all duration-300 group-hover:-bottom-3 group-hover:-right-3 group-hover:text-accent-gold">
              <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-current" />
              <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-current" />
            </span>

            <Link to={product.link || "#"} className="block">
              <div className="relative aspect-[4/5] overflow-hidden rounded-lg bg-secondary shadow-md">
                {product.featured_image_url && (
                  <img
                    src={product.featured_image_url}
                    alt={product.title}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]"
                  />
                )}
                {product.discounted_price ? (
                  <Badge className="absolute left-3 top-3 rounded-lg border-border bg-white-pure font-default text-[10px] uppercase tracking-[0.16em] text-sage-deep">
                    Sale
                  </Badge>
                ) : null}
              </div>
            </Link>

            <div className="mt-5 space-y-1">
              <p className="font-default text-[13px] italic text-sage-deep/70 transition-colors duration-300 group-hover:text-accent-gold">
                B.Y.E. Hygiene Mist™
              </p>
              <Link to={product.link || "#"}>
                <h3 className="font-default text-base font-medium text-sage-deep transition-colors hover:text-accent-gold">
                  {product.title}
                </h3>
              </Link>
              <div className="flex items-baseline gap-2 pt-1">
                {product.discounted_price ? (
                  <>
                    <span className="font-default text-sm text-muted-foreground line-through">
                      ${product.price.toFixed(2)}
                    </span>
                    <span className="font-default text-sm text-sage-deep underline decoration-accent-gold decoration-1 underline-offset-4">
                      ${product.discounted_price.toFixed(2)}
                    </span>
                  </>
                ) : (
                  <span className="font-default text-sm text-sage-deep">
                    <span className="text-xs text-muted-foreground">$</span>
                    {product.price.toFixed(2)}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-4 space-y-2 overflow-hidden">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  setSelectedProduct({
                    id: product.id,
                    title: product.title,
                    short_description: product.short_description,
                    long_description: product.long_description,
                    price: product.discounted_price || product.price,
                    featured_image_url: product.featured_image_url,
                    link: product.link,
                  })
                }
                className="w-full translate-y-2 rounded-lg border-sage-deep bg-transparent font-default text-[11px] uppercase tracking-[0.16em] text-sage-deep opacity-0 transition-all duration-300 hover:bg-sage-deep hover:text-white-pure group-hover:translate-y-0 group-hover:opacity-100"
              >
                Quick View
              </Button>
              <AddToCartButton
                productId={product.id}
                variant="default"
                className="w-full"
                buttonClassName="bg-accent-gold text-white-pure rounded-lg font-default text-[11px] uppercase tracking-[0.16em]"
                text="Add to Cart"
                showQuantity={false}
              />
            </div>
          </article>
        ))}
      </div>

      <PaginationControls />

      <Dialog
        open={!!selectedProduct}
        onOpenChange={(open) => !open && setSelectedProduct(null)}
      >
        <DialogContent className="max-w-3xl rounded-lg bg-white-pure p-0 shadow-lg">
          {selectedProduct && (
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="aspect-[4/5] overflow-hidden rounded-l-lg bg-secondary md:aspect-auto">
                {selectedProduct.featured_image_url && (
                  <img
                    src={selectedProduct.featured_image_url}
                    alt={selectedProduct.title}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                )}
              </div>
              <div className="flex flex-col p-8">
                <DialogHeader className="space-y-2">
                  <p className="font-default text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    § — Product Details
                  </p>
                  <p className="font-default text-sm italic text-sage-deep/70">
                    B.Y.E. Hygiene Mist™
                  </p>
                  <DialogTitle className="font-default text-2xl font-normal leading-tight tracking-tight text-sage-deep">
                    {selectedProduct.title}
                  </DialogTitle>
                </DialogHeader>

                <div className="mt-4 font-default text-lg text-sage-deep">
                  <span className="text-sm text-muted-foreground">$</span>
                  {selectedProduct.price.toFixed(2)}
                </div>

                <div className="mt-4 h-px w-full bg-border">
                  <span className="ml-auto block h-1.5 w-1.5 -translate-y-[3px] rounded-lg bg-accent-gold" />
                </div>

                {selectedProduct.short_description && (
                  <div
                    className="mt-4 text-sm leading-relaxed text-sage-deep/80 [&_p:last-child]:mb-0 [&_p]:mb-2"
                    dangerouslySetInnerHTML={{
                      __html: selectedProduct.short_description,
                    }}
                  />
                )}

                <div className="mt-auto space-y-3 pt-6">
                  <AddToCartButton
                    productId={selectedProduct.id}
                    variant="default"
                    className="w-full"
                    buttonClassName="bg-accent-gold text-white-pure rounded-lg font-default text-xs uppercase tracking-[0.16em]"
                    text="Add to Cart"
                    showQuantity={true}
                  />
                  <Link
                    to={selectedProduct.link || "#"}
                    className="block w-full text-center font-default text-[11px] uppercase tracking-[0.16em] text-sage-deep underline decoration-border underline-offset-[6px] transition-all hover:decoration-accent-gold hover:underline-offset-4"
                  >
                    View Full Details
                  </Link>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function PaginationControls() {
  const {
    hasNext,
    hasPrev,
    currentPage,
    totalPages,
    nextPage,
    prevPage,
    setPage,
  } = useEcommerceProducts();

  if (totalPages <= 1) return null;

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1).slice(
    Math.max(0, currentPage - 3),
    Math.max(0, currentPage - 3) + 5
  );

  return (
    <nav className="mt-16 flex items-center justify-center gap-2 border-t border-border pt-8">
      <Button
        type="button"
        variant="ghost"
        onClick={prevPage}
        disabled={!hasPrev}
        className="rounded-lg font-default text-[11px] uppercase tracking-[0.16em] text-sage-deep hover:bg-transparent hover:text-accent-gold disabled:opacity-30"
      >
        ← Prev
      </Button>
      <div className="flex items-center gap-1">
        {pages.map((p, i) => (
          <Button
            key={p}
            data-index={i}
            type="button"
            variant="ghost"
            onClick={() => setPage(p)}
            className={
              p === currentPage
                ? "h-9 w-9 rounded-lg border border-accent-gold p-0 font-default text-xs text-sage-deep"
                : "h-9 w-9 rounded-lg p-0 font-default text-xs text-muted-foreground hover:bg-transparent hover:text-sage-deep"
            }
          >
            {p}
          </Button>
        ))}
      </div>
      <Button
        type="button"
        variant="ghost"
        onClick={nextPage}
        disabled={!hasNext}
        className="rounded-lg font-default text-[11px] uppercase tracking-[0.16em] text-sage-deep hover:bg-transparent hover:text-accent-gold disabled:opacity-30"
      >
        Next →
      </Button>
    </nav>
  );
}

function SidebarHeading() {
  return (
    <div className="mb-6 space-y-2 border-b border-border pb-4">
      <p className="font-default text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        § — Filters
      </p>
      <h2 className="font-default text-2xl font-normal leading-tight tracking-tight text-sage-deep">
        Refine the <span className="italic">collection</span>
      </h2>
    </div>
  );
}

export default function Shop() {
  return (
    <section data-section-id="2038"
      id="shop-content"
      className="relative bg-background py-12 md:py-20"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="mb-10 md:mb-14">
          <p className="font-default text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            § 01 — Apothecary
          </p>
          <h1 className="mt-3 font-default text-4xl font-normal leading-[0.95] tracking-tight text-sage-deep md:text-6xl">
            The Curated <br />
            <span className="italic">Shop</span>
          </h1>
          <div className="mt-4 flex items-center gap-3">
            <div className="h-px w-24 bg-border" />
            <span className="block h-1.5 w-1.5 rounded-lg bg-accent-gold" />
          </div>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="flex flex-col gap-8 lg:flex-row lg:gap-10">
            <aside className="hidden w-full shrink-0 lg:block lg:w-1/4">
              <div className="sticky top-24 relative rounded-lg bg-white-pure p-7 shadow-md">
                <span className="pointer-events-none absolute -left-1.5 -top-1.5 h-3 w-3 text-border">
                  <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-current" />
                  <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-current" />
                </span>
                <span className="pointer-events-none absolute -right-1.5 -top-1.5 h-3 w-3 text-border">
                  <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-current" />
                  <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-current" />
                </span>
                <span className="pointer-events-none absolute -bottom-1.5 -left-1.5 h-3 w-3 text-border">
                  <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-current" />
                  <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-current" />
                </span>
                <span className="pointer-events-none absolute -bottom-1.5 -right-1.5 h-3 w-3 text-border">
                  <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-current" />
                  <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-current" />
                </span>

                <SidebarHeading />
                <ProductsFilter />
              </div>
            </aside>

            <div className="w-full lg:w-3/4">
              <div className="mb-8 flex flex-wrap items-center justify-between gap-4 border-b border-border pb-5">
                <div className="flex items-center gap-3">
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button
                        variant="outline"
                        className="rounded-lg border-sage-deep bg-transparent font-default text-[11px] uppercase tracking-[0.16em] text-sage-deep hover:bg-sage-deep hover:text-white-pure lg:hidden"
                      >
                        <SlidersHorizontal className="mr-2 h-3.5 w-3.5" />
                        Filters
                      </Button>
                    </SheetTrigger>
                    <SheetContent
                      side="left"
                      className="w-[320px] overflow-y-auto bg-background sm:w-[380px]"
                    >
                      <SheetHeader>
                        <SheetTitle className="font-default text-2xl font-normal tracking-tight text-sage-deep">
                          <span className="italic">Filters</span>
                        </SheetTitle>
                      </SheetHeader>
                      <div className="px-4 pb-8">
                        <ProductsFilter />
                      </div>
                    </SheetContent>
                  </Sheet>

                  <p className="hidden font-default text-[11px] uppercase tracking-[0.18em] text-muted-foreground sm:block">
                    Browse all
                  </p>
                </div>

                <ProductSortDropdown label="Sort by" />
              </div>

              <ProductsGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}